import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { tr } from "../lib/i18n";

const input = "mt-2 w-full h-12 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 text-lg tracking-[0.3em] text-center outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-ring)]";

// Second sign-in step: first-time authenticator setup, or the 6-digit code / a recovery code.
export const TwoFactorStep = ({ stage, mfaToken, onDone, onBack }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (stage !== "setup") return;
    api.post("/auth/2fa/setup/start", { mfa_token: mfaToken }).then(({ data }) => setSetup(data)).catch((e) => { toast.error(formatApiError(e)); onBack(); });
  }, [stage, mfaToken, onBack]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(stage === "setup" ? "/auth/2fa/setup/confirm" : "/auth/2fa/verify", { mfa_token: mfaToken, code });
      if (data.recovery_codes) { setCodes(data.recovery_codes); setResult(data); }
      else {
        if (data.recovery_codes_left != null) toast.message(tr("mfa.codes_left", { n: data.recovery_codes_left }));
        onDone(data);
      }
    } catch (err) {
      toast.error(err?.response?.status === 401 ? tr("mfa.wrong_code") : formatApiError(err));
      setCode("");
    } finally { setBusy(false); }
  };

  if (codes) return <RecoveryCodes codes={codes} onContinue={() => onDone(result)} />;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-form">
      <div>
        <div className="eyebrow mb-2">{tr("mfa.eyebrow")}</div>
        <h2 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>{stage === "setup" ? tr("mfa.setup_title") : tr("mfa.verify_title")}</h2>
        <p className="text-sm text-[var(--gray-400)] mt-1">{stage === "setup" ? tr("mfa.setup_help") : useRecovery ? tr("mfa.recovery_help") : tr("mfa.verify_help")}</p>
      </div>
      {stage === "setup" && setup && (
        <div className="flex flex-col sm:flex-row gap-4 items-center p-4 border rounded-lg" data-testid="mfa-setup-box">
          <img src={setup.qr_png} alt={tr("mfa.qr_alt")} width={168} height={168} className="bg-white p-1 shrink-0" data-testid="mfa-qr" />
          <div className="min-w-0 text-sm">
            <div className="kpi-label">{tr("mfa.manual_key")}</div>
            <bdi dir="ltr" className="block font-mono text-sm break-all mt-1 select-all" data-testid="mfa-manual-key">{setup.manual_key}</bdi>
          </div>
        </div>
      )}
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gray-400)]">{useRecovery ? tr("mfa.recovery_label") : tr("mfa.code_label")}</label>
        <input data-testid="mfa-code" dir="ltr" autoFocus autoComplete="one-time-code" inputMode={useRecovery ? "text" : "numeric"}
          maxLength={useRecovery ? 11 : 6} value={code} onChange={(e) => setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))} className={input} required />
      </div>
      <button data-testid="mfa-submit" type="submit" disabled={busy || (!useRecovery && code.length !== 6)} className="w-full h-11 rounded-lg bg-accent-brand font-semibold text-sm disabled:opacity-60">
        {busy ? tr("x.signing_in") : stage === "setup" ? tr("mfa.confirm") : tr("mfa.verify")}
      </button>
      <div className="flex justify-between gap-3 text-sm flex-wrap">
        <button type="button" className="text-[var(--gray-400)] underline" onClick={onBack} data-testid="mfa-back">{tr("mfa.back")}</button>
        {stage === "verify" && (
          <button type="button" className="text-accent-brand underline" onClick={() => { setUseRecovery((v) => !v); setCode(""); }} data-testid="mfa-toggle-recovery">
            {useRecovery ? tr("mfa.use_app") : tr("mfa.use_recovery")}
          </button>
        )}
      </div>
    </form>
  );
};

const RecoveryCodes = ({ codes, onContinue }) => {
  const [saved, setSaved] = useState(false);
  const text = codes.join("\n");
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text + "\n"], { type: "text/plain" }));
    a.download = "showroom360-recovery-codes.txt";
    a.click();
    setSaved(true);
  };
  const copy = async () => { await navigator.clipboard.writeText(text); toast.success(tr("mfa.copied")); setSaved(true); };
  return (
    <div className="space-y-5" data-testid="mfa-recovery-codes">
      <div>
        <h2 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>{tr("mfa.codes_title")}</h2>
        <p className="text-sm text-[var(--gray-400)] mt-1">{tr("mfa.codes_help")}</p>
      </div>
      <ol dir="ltr" className="grid grid-cols-2 gap-2 font-mono text-sm p-4 border rounded-lg bg-[hsl(var(--card))]">
        {codes.map((c) => <li key={c} data-testid="mfa-recovery-code">{c}</li>)}
      </ol>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={download} className="h-10 rounded-lg border text-sm font-semibold" data-testid="mfa-codes-download">{tr("mfa.download")}</button>
        <button type="button" onClick={copy} className="h-10 rounded-lg border text-sm font-semibold" data-testid="mfa-codes-copy">{tr("mfa.copy")}</button>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="mfa-codes-saved" /> {tr("mfa.saved_them")}</label>
      <button type="button" disabled={!saved} onClick={onContinue} className="w-full h-11 rounded-lg bg-accent-brand font-semibold text-sm disabled:opacity-60" data-testid="mfa-codes-continue">{tr("mfa.continue")}</button>
    </div>
  );
};
